import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { LoadingBlock } from '@sf/ui';
import { useApi } from '../api/ApiProvider';
import { useAuth } from './AuthProvider';
import { blocksPublicRoute } from '../demo/session';
import { registerRedirectTarget, returnToFor, targetForAlreadyAuthenticated, withReturnTo } from './returnTo';

/**
 * Route guard for everything behind the login. Anonymous visitors are sent to /login with the
 * attempted path and query string attached as `?from=` (see returnTo.ts) — never router state, which
 * cannot survive the full-page round trip SSO requires — so every sign-in path can bounce them back.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <LoadingBlock label="Loading your account" />;
  if (status === 'anonymous') {
    const to = withReturnTo('/login', returnToFor(location.pathname, location.search));
    return <Navigate to={to} replace />;
  }
  return <>{children}</>;
}

/**
 * The mirror image: keeps signed-in users away from /login and /register.
 *
 * A demo session is the exception, and it is dropped rather than defended — see `blocksPublicRoute`.
 * Someone who looked around and then pressed "Create account" is asking to leave the demo, and the
 * tokens go with the decision; the page they asked for renders in the same breath.
 */
export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { status, user } = useAuth();
  const api = useApi();
  const location = useLocation();

  const leavingDemo = status === 'authenticated' && user?.demo === true;
  useEffect(() => {
    if (leavingDemo) api.setTokens(null);
  }, [api, leavingDemo]);

  if (status === 'loading') return <LoadingBlock />;
  if (blocksPublicRoute(status, user?.demo)) {
    // Already signed in and opened /login or /register anyway — a stale tab, a forwarded link. Send
    // them exactly where signing in there would have: `?from=` when the address carried one, otherwise
    // the onboarding wizard from /register or the dashboard from anywhere else.
    //
    // /register shares registerRedirectTarget with RegisterPage's own post-submit redirect instead of
    // computing its own answer here — found on review: this component's status flip and RegisterPage's
    // own navigate() can each render from a different snapshot of location.search for a few
    // milliseconds right after registration (BrowserRouter defers this component's location updates
    // through a React transition; AuthProvider's status flip is not deferred), so the two could
    // disagree on the destination and let a freshly created account skip onboarding — exactly what
    // rule 6 exists to prevent. See registerRedirectTarget's own doc comment in returnTo.ts.
    const target =
      location.pathname === '/register'
        ? registerRedirectTarget(location.search)
        : targetForAlreadyAuthenticated(location.search, '/');
    return <Navigate to={target} replace />;
  }
  return <>{children}</>;
}
